import React from 'react';
import { C, CIRCLE_R, nx, ny } from '../flowCanvas.constants';

export default function CircleNode({ step, selected, onClick, centreY, overrideX, overrideY }) {
  const x = overrideX ?? nx(step), y = overrideY ?? ny(step, centreY);
  const isStart = step.stepId === 'start_node' || (step.layout?.row ?? null) === 0;
  const isEnd   = !isStart && (
    step.stepId?.endsWith('_ended_node') ||
    (step.stepId !== 'previous_result_hidden_node' && (!step.connectsTo || step.connectsTo.length === 0))
  );

  const title = (step.stepTitle || '').trim();

  // Prefer single-line titles; wrap only when it truly doesn't fit.
  // Circle diameter is ~116px — keep some breathing room from the stroke.
  const INNER_TEXT_W = CIRCLE_R * 2 - 22;
  const words = title.length ? title.split(/\s+/) : [];

  const truncate = (s, max) => (s.length > max ? s.slice(0, Math.max(0, max - 1)) + '…' : s);

  // Rough width estimator (px) for the current font weight.
  // Treat spaces narrower than letters so wrapping is less aggressive.
  const estimateTextWidth = (text, fontSize) => {
    let units = 0;
    for (const ch of (text || '')) {
      if (ch === ' ') units += 0.33;
      else units += 0.62;
    }
    return units * fontSize;
  };

  const fits = (text, fontSize) => estimateTextWidth(text, fontSize) <= INNER_TEXT_W;

  // If it *almost* fits, prefer a slightly smaller font before wrapping.
  const pickSingleLineFont = (text) => {
    if (fits(text, 11)) return 11;
    if (fits(text, 10.5)) return 10.5;
    if (fits(text, 10)) return 10;
    return null;
  };

  // Wrap into up to maxLines, greedy by words, falling back to truncation.
  const wrapWords = (w, fontSize, maxLines) => {
    if (!w.length) return [''];

    const lines = [];
    let i = 0;
    while (i < w.length && lines.length < maxLines) {
      let line = '';
      while (i < w.length) {
        const next = line ? `${line} ${w[i]}` : w[i];
        if (fits(next, fontSize)) {
          line = next;
          i++;
          continue;
        }
        break;
      }

      // If we couldn't fit even a single word (very long token), truncate it.
      if (!line) {
        const token = w[i];
        let cut = token;
        while (cut.length > 1 && !fits(cut + '…', fontSize)) cut = cut.slice(0, -1);
        line = cut.length > 1 ? (cut + '…') : '…';
        i++;
      }

      lines.push(line);
    }

    // Still have remaining words → append to last line with truncation.
    if (i < w.length && lines.length) {
      const rest = w.slice(i).join(' ');
      let base = lines[lines.length - 1];
      let merged = base ? `${base} ${rest}` : rest;
      if (!fits(merged, fontSize)) {
        // Truncate merged to fit.
        let cut = merged;
        while (cut.length > 1 && !fits(cut + '…', fontSize)) cut = cut.slice(0, -1);
        merged = cut.length > 1 ? (cut + '…') : '…';
      }
      lines[lines.length - 1] = merged;
    }

    return lines;
  };

  const render = (() => {
    if (!title) return { lines: [], fontSize: 11 };

    const singleFont = pickSingleLineFont(title);
    if (singleFont) return { lines: [title], fontSize: singleFont };

    // Wrap for longer titles.
    // 2 lines first; if the second line would be too truncated, allow 3 lines.
    const fontSize = 10.5;
    const two = wrapWords(words, fontSize, 2);
    const looksTooTruncated = two.length === 2 && /…$/.test(two[1]) && two[1].length < 8;
    const lines = looksTooTruncated ? wrapWords(words, fontSize, 3) : two;
    return { lines, fontSize };
  })();

  return (
    <g className="fc-node fc-node--circle" onClick={onClick} style={{ cursor: 'pointer' }}>
      {(selected || isStart || isEnd) && (
        <circle cx={x} cy={y} r={CIRCLE_R + 9}
          fill="none"
          stroke={selected ? C.sel : isStart ? C.green : C.red}
          strokeWidth={selected ? 1.5 : 1}
          strokeOpacity={selected ? 0.8 : 0.4}
          strokeDasharray={selected ? undefined : '4 3'}
        />
      )}
      <circle cx={x} cy={y} r={CIRCLE_R}
        fill={C.circle}
        stroke={selected ? C.sel : isStart ? C.green : isEnd ? C.red : C.purple}
        strokeWidth={selected ? 2 : (isStart || isEnd) ? 2 : 1.5}
      />
      {render.lines.length === 1 && (
        <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
          fontSize={render.fontSize} fontWeight="700"
          fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {render.lines[0]}
        </text>
      )}

      {render.lines.length === 2 && (
        <>
          <text x={x} y={y - 7} textAnchor="middle" fontSize={render.fontSize} fontWeight="700"
            fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {render.lines[0]}
          </text>
          <text x={x} y={y + 8} textAnchor="middle" fontSize={render.fontSize} fontWeight="700"
            fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {render.lines[1]}
          </text>
        </>
      )}

      {render.lines.length >= 3 && (
        <>
          <text x={x} y={y - 12} textAnchor="middle" fontSize={render.fontSize} fontWeight="700"
            fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {render.lines[0]}
          </text>
          <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
            fontSize={render.fontSize} fontWeight="700"
            fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {render.lines[1]}
          </text>
          <text x={x} y={y + 14} textAnchor="middle" fontSize={render.fontSize} fontWeight="700"
            fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {truncate(render.lines[2], 28)}
          </text>
        </>
      )}
    </g>
  );
}
